import { useCallback, useState } from 'react';
import { api } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { EmptyState } from '../../components/EmptyState';
import { Table, type Column } from '../../components/Table';
import { errorMessage, formatDate } from '../members/hooks';
import type { UnassignedSlot } from './types';

interface Page {
  items: UnassignedSlot[];
  nextCursor: string | null;
}

export function MigrationPage() {
  const [items, setItems] = useState<UnassignedSlot[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (from: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ limit: '100' });
      if (from) qs.set('cursor', from);
      const page = await api.get<Page>(`/v1/operator/migration/unassigned?${qs}`);
      setItems((prev) => (from ? [...prev, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setStarted(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const columns: Column<UnassignedSlot>[] = [
    { key: 'code', header: 'Código', mono: true, render: (s) => s.code },
    { key: 'domain', header: 'Dominio', render: (s) => s.domain },
    { key: 'client', header: 'clientId', mono: true, render: (s) => s.clientId ?? <span className="text-ink-muted">(vacío)</span> },
    { key: 'type', header: 'Tipo', render: (s) => s.type ?? s.kind },
    { key: 'active', header: 'Estado', render: (s) => (s.active ? <Chip tone="success">Activo</Chip> : <Chip>Inactivo</Chip>) },
    { key: 'hits', header: 'Escaneos', align: 'right', render: (s) => <span className="tabular-nums">{s.hits}</span> },
    { key: 'created', header: 'Creado', render: (s) => formatDate(s.createdAt) },
  ];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">Migración</h1>
          <p className="text-sm text-ink-muted">Slots anteriores a las organizaciones que no tienen dueño identificable.</p>
        </div>
        <Button onClick={() => void load(null)} loading={loading && !started}>{started ? 'Actualizar' : 'Buscar slots sin dueño'}</Button>
      </div>
      <Banner tone="info">
        La asignación se aplica con el script de migración (<code className="font-mono">scripts/migrate_etapa1.py --apply</code>), que es idempotente y reanudable. Esta lista sirve para revisar los pendientes.
      </Banner>
      <div aria-live="polite">{error && <Banner tone="error">{error}</Banner>}</div>
      {started ? (
        <>
          <Table
            caption="Slots sin organización"
            columns={columns}
            rows={items}
            rowKey={(s) => s.id}
            loading={loading && items.length === 0}
            empty={<EmptyState title="Sin pendientes" description="Todos los slots tienen organización asignada." />}
          />
          {cursor && (
            <div className="flex justify-center">
              <Button variant="secondary" loading={loading} onClick={() => void load(cursor)}>Cargar más</Button>
            </div>
          )}
        </>
      ) : (
        <EmptyState title="Revisión bajo demanda" description="La búsqueda recorre la colección completa de slots; ejecútala cuando la necesites." />
      )}
    </section>
  );
}
